import { itemByLocales } from '@monoimin/intl';

import type { FiltersState, SelectItem } from '../types/filters';
import type { StrapiEstate } from '@monoimin/sb-types';

export const BATHROOMS = [
  { id: 1, title: '1', value: '1' },
  { id: 2, title: '2', value: '2' },
  { id: 3, title: '3', value: '3' },
  { id: 4, title: '4', value: '4' },
  { id: 5, title: '5', value: '5' },
  { id: 6, title: '6', value: '6' },
  { id: 7, title: '7', value: '7' },
  { id: 8, title: '8', value: '8' },
];

export const BEDROOMS = [
  { id: 1, title: '1', value: '1' },
  { id: 2, title: '2', value: '2' },
  { id: 3, title: '3', value: '3' },
  { id: 4, title: '4', value: '4' },
  { id: 5, title: '5', value: '5' },
];

export const MIN_PRICE = 45000;

export const MAX_PRICE = 5000000;

export const EMPTY_FILTERS_STATE: FiltersState = {
  country: [],
  city: [],
  object_type: [],
  bedrooms: [],
  price_from: null,
  price_to: null,
};

export const ESTATE_UNIT_FILTER_KEYS = {
  COUNTRY: 'country',
  CITY: 'city',
  OBJECT_TYPE: 'object_type',
  BEDROOMS: 'bedrooms',
  ESTATE_TYPE: 'estate_type',
  OBJECT_STAGE: 'object_stage',
  OBJECT_CATEGORY: 'object_category',
  BATHROOMS: 'bathrooms',
  FURNITURE: 'furniture',
  PARKING: 'parking',
  VIEW: 'view',
  DESIGN_FEATURE: 'design_feature',
  SEARCH: 'search',
  IS_ISLAND: 'is_island',
  BEST_OFFER: 'best_offer',
  DEVELOPER: 'developer',
  ELIGIBLE_FOR_PROGRAM: 'eligible_for_program',
  COST_FOR_GR: 'cost_for_gr',
} as const;

export const ESTATE_RANGE_FROM_FILTER_KEYS = {
  PRICE_FROM: 'price_from',
  RENTAL_YIELD_FROM: 'rental_yield_from',
  OCEAN_FROM: 'ocean_from',
} as const;

export const ESTATE_RANGE_TO_FILTER_KEYS = {
  PRICE_TO: 'price_to',
  RENTAL_YIELD_TO: 'rental_yield_to',
  OCEAN_TO: 'ocean_to',
} as const;

export const ESTATE_RANGE_FILTER_KEYS = Object.assign(
  ESTATE_RANGE_FROM_FILTER_KEYS,
  ESTATE_RANGE_TO_FILTER_KEYS
);

export const ESTATE_FILTER_KEYS = Object.assign(
  ESTATE_UNIT_FILTER_KEYS,
  ESTATE_RANGE_FILTER_KEYS
);

export const ESTATE_FILTER_KEYS_ARRAY = Object.values(ESTATE_FILTER_KEYS);

export const DISABLED_SEARCH_CONFIG = {
  disabled: true,
};

export const EMPTY_FILTER_ITEMS = itemByLocales<SelectItem[]>([]);

export const ESTATES_PAGE_SIZE = 9;

export const EMPTY_ESTATES_ARRAY: Array<StrapiEstate> = [];

export const GET_COUNTRY_PROGRAMS_EMPTY_STATE = { europe: [], caribbean: [] };

export const GET_ALL_TERRITORIES_PAGINATION = { page: 1, pageSize: 300 };

export const GET_ALL_DATA_PAGINATION = { page: 1, pageSize: 1000 };

export const PROPERTY_EMPTY_IMAGE = {
  src: '',
  alt: '',
};

export const PREPARE_PROGRAM_FACTS_EMPTY_STATE = { time: '', investment: '' };

export const PUBLIC_ESTATE_FILTERS = {
  status: 'for-sale',
  is_hidden: false,
};

export const MANAGERS_ESTATE_FILTERS = {
  status: PUBLIC_ESTATE_FILTERS.status,
  is_hidden: {
    $in: ['true', 'false'],
  },
};

export const MANAGERS_PAGE_TOKEN = 'ejttt3vkjr87b73';

export const GET_ESTATES_FIELDS = [
  'bedrooms_number',
  'bathrooms_number',
  'object_type',
  'currency',
  'title',
  'slug',
  'publishedAt',
];

export const GET_ESTATES_POPULATE = [
  'price',
  'total_area',
  'country',
  'city',
  'main_image',
  'images',
];

export enum ESTATE_SORTING_VALUES {
  CHEAPER = 'cheaper',
  EXPENSIVE = 'expensive',
  NEWER = 'newer',
  OLDER = 'older',
  FAVORABLE = 'favorable',
  PRICE_ASC = 'price_asc',
  PRICE_DESC = 'price_desc',
  PUBLISHED_AT_ASC = 'published_at_asc',
  PUBLISHED_AT_DESC = 'published_at_desc',
  TOTAL_AREA_ASC = 'total_area_asc',
  TOTAL_AREA_DESC = 'total_area_desc',
}

export const ESTATE_SORTING_PARAMS: { [key in ESTATE_SORTING_VALUES]: string } =
  {
    cheaper: 'price.from',
    expensive: 'price.from:desc',
    newer: 'publishedAt',
    older: 'publishedAt:desc',
    favorable: 'is_favorable',
    price_asc: 'price.from:asc',
    price_desc: 'price.from:desc',
    published_at_asc: 'publishedAt:asc',
    published_at_desc: 'publishedAt:desc',
    total_area_asc: 'total_area.from:asc',
    total_area_desc: 'total_area.from:desc',
  };

export enum ESTATE_PAGE_ROLES {
  OBJECT_STAGE = 'object-stage',
  PROPERTY_TYPE = 'property-type',
  COUNTRY_PROPERTY_TYPE = 'country-property-type',
  PROPERTY = 'property',
  COUNTRY = 'country',
}

/**
 * `from` – `to` fields
 */
export const rangeFields = [
  'price',
  'total_area',
  'residential_area',
  'kitchen_area',
  'plot_area',
  'open_terrace_area',
  'closed_terrace_area',
  'common_zones_area',
  'apr',
  'bedrooms_number_range',
  'bathrooms_number_range',
  'rooms_number_range',
  'max_floor_range',
  'floors_range',
  'parking_places_number_range',
] as const;

export const FROM_TO_FILTER_TEMPLATE = {
  from: {
    $gte: undefined,
  },
  to: {
    $lte: undefined,
  },
};

export const STRAPI_FILTER_OPERATORS = {
  IN: '$in',
  CONTAINSI: '$containsi',
  STARTS_WITHI: '$startsWithi',
  AND: '$and',
  OR: '$or',
  BETWEEN: '$between',
  GTE: '$gte',
  LTE: '$lte',
} as const;

export enum FILTER_VARIANTS {
  SELECT = 'select',
  RANGE = 'range',
  INPUT = 'input',
  OPTION = 'option',
}

export const EMPTY_OBJECT = Object.create({});

export const REAL_ESTATE_LIST_CLASSES = {
  container: 'real-estate-list flex flex-col gap-4 lg:gap-10',
  sortingContainer:
    'flex flex-col gap-3 lg:flex-row justify-between lg:items-start',
  cardsContainer: {
    row: 'flex flex-col gap-10 lg:gap-9',
    column: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5',
  },
} as const;
